import { useEffect, useReducer, useRef } from 'react'
import { fanYMax } from '../chart/fanDomain'
import { lerpInto, smoothingFactor } from '../chart/geometry'
import type { SimResult } from '../engine/types'

/**
 * Smoothly animates the fan between simulation results.
 *
 * Two behaviours the brief asks for come out of this one hook:
 *
 *  - **The fan opens.** The first result animates out of a flat line at the
 *    starting corpus rather than appearing fully formed.
 *  - **The fan morphs, never cuts.** Dragging a slider produces a new result
 *    several times a second; each one is a new *target* that the current shape
 *    eases toward, so the chart reads as one continuous surface being deformed
 *    rather than a slideshow.
 *
 * The y-axis maximum is animated alongside the bands. Animating the data while
 * snapping the scale would make the fan appear to jump in the opposite
 * direction to the change that caused it.
 */

export interface FanFrame {
  ages: Float32Array
  p10: Float32Array
  p25: Float32Array
  p50: Float32Array
  p75: Float32Array
  p90: Float32Array
  required: Float32Array
  yMax: number
  /** True once the shape has caught up with the latest result. */
  settled: boolean
}

const SERIES = ['p10', 'p25', 'p50', 'p75', 'p90', 'required'] as const

/**
 * How long one continuous animation run may take before it is snapped to its
 * target and stopped.
 *
 * Generous by design — the ease itself lands in a few hundred milliseconds even
 * on a slow phone, so this is not a deadline the normal path can miss. It exists
 * only to bound the pathological case.
 */
const SETTLE_BUDGET_MS = 2_000
type SeriesKey = (typeof SERIES)[number]

interface AnimationState {
  current: Record<SeriesKey, Float32Array>
  target: Record<SeriesKey, Float32Array>
  ages: Float32Array
  currentYMax: number
  targetYMax: number
  settled: boolean
}

/**
 * Stretches a series onto a new length, preserving its shape.
 *
 * Linear interpolation over the normalised index — the curve is sampled at the
 * same relative positions, so a 41-point fan becoming 40 points keeps looking
 * like the same fan rather than restarting. Exact at both endpoints.
 */
function resample(source: Float32Array, length: number): Float32Array {
  const out = new Float32Array(length)
  if (source.length === 0 || length === 0) return out
  if (source.length === 1) return out.fill(source[0])

  const scale = (source.length - 1) / Math.max(1, length - 1)
  for (let i = 0; i < length; i++) {
    const position = i * scale
    const lower = Math.floor(position)
    const upper = Math.min(source.length - 1, lower + 1)
    const fraction = position - lower
    out[i] = source[lower] + (source[upper] - source[lower]) * fraction
  }
  return out
}

function targetsFrom(result: SimResult): Record<SeriesKey, Float32Array> {
  return {
    p10: result.bands.p10,
    p25: result.bands.p25,
    p50: result.bands.p50,
    p75: result.bands.p75,
    p90: result.bands.p90,
    required: result.requiredMedian,
  }
}

export function useAnimatedFan(
  result: SimResult | null,
  tau = 90,
  /**
   * Y-domain to ease toward, overriding this result's own `fanYMax`.
   *
   * Several fans on one chart must share an axis, and the right value is a
   * property of the SET — see `fanYMaxAcross`. Passing it in rather than taking
   * the max of two independently-animating values matters: each hook would
   * otherwise ease its own target on its own curve, and the max of two curves is
   * continuous but not smooth, so the axis visibly changes pace mid-transition.
   */
  yMaxOverride?: number,
): FanFrame | null {
  const stateRef = useRef<AnimationState | null>(null)
  const frameRef = useRef<number | null>(null)
  const lastTimeRef = useRef(0)
  /** Timestamp the current run began, for the settle budget. 0 while idle. */
  const startedAtRef = useRef(0)
  const [, forceRender] = useReducer((c: number) => c + 1, 0)

  // Adopt each new result as the target.
  useEffect(() => {
    /*
     * A null result must CLEAR the state, not just skip the update.
     *
     * `FanChart` calls this hook twice by array position, so slot 1 is reused by
     * whatever layer appears next. Early-returning here left the removed
     * scenario's six Float32Arrays and its `yMax` in `stateRef`, and the tail of
     * this hook kept returning them — so dropping a comparison and adding one
     * back opened the new fan on the deleted fan's geometry, and fed the axis a
     * scale belonging to a scenario that no longer existed.
     */
    if (result === null) {
      if (stateRef.current !== null) {
        stateRef.current = null
        if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
        frameRef.current = null
        forceRender()
      }
      return
    }

    const targets = targetsFrom(result)
    const nextYMax = yMaxOverride ?? fanYMax(result)
    const previous = stateRef.current
    const lengthChanged = previous === null || previous.ages.length !== result.ages.length

    if (lengthChanged) {
      const length = result.ages.length
      const current = {} as Record<SeriesKey, Float32Array>

      if (previous === null) {
        // First result: open the fan out of a flat line at the starting corpus.
        const start = result.bands.p50[0]
        for (const key of SERIES) current[key] = new Float32Array(length).fill(start)
      } else {
        // The horizon changed, so the arrays no longer line up point-for-point.
        // Resample the shape we are currently showing onto the new length instead
        // of resetting it.
        //
        // This previously refilled flat, which meant every length change collapsed
        // the fan and re-opened it. That fires far more often than it sounds:
        // `years = projectToAge - currentAge`, so dragging the age slider changes
        // the length on EVERY tick, and the chart was visibly collapsing and
        // re-expanding through the whole gesture.
        for (const key of SERIES) current[key] = resample(previous.current[key], length)
      }
      stateRef.current = {
        current,
        target: targets,
        ages: result.ages,
        currentYMax: nextYMax,
        targetYMax: nextYMax,
        settled: false,
      }
    } else {
      previous.target = targets
      previous.ages = result.ages
      previous.targetYMax = nextYMax
      previous.settled = false
    }

    /*
     * Restart the settle budget on every new target, not just when the loop
     * wakes from idle.
     *
     * The budget asks "is this still easing toward the CURRENT target", so it
     * has to reset when the target moves. Tying it to the loop instead would
     * punish a long drag: the loop never goes idle mid-gesture, so after two
     * seconds of continuous dragging every frame would snap and the morph the
     * chart is built around would just stop happening.
     */
    startedAtRef.current = 0
    if (frameRef.current === null) {
      lastTimeRef.current = 0
      frameRef.current = requestAnimationFrame(step)
    }
    forceRender()

    function step(time: number): void {
      const state = stateRef.current
      if (state === null) {
        frameRef.current = null
        return
      }

      // First frame has no previous timestamp to difference against.
      const deltaMs =
        lastTimeRef.current === 0 ? 16.7 : Math.min(64, time - lastTimeRef.current)
      lastTimeRef.current = time
      if (startedAtRef.current === 0) startedAtRef.current = time

      const k = smoothingFactor(deltaMs, tau)
      // Absolute epsilon scaled to the axis: below this a change is sub-pixel.
      const epsilon = Math.max(1, state.targetYMax * 1e-4)

      let settled = true
      for (const key of SERIES) {
        if (!lerpInto(state.current[key], state.target[key], k, epsilon)) settled = false
      }

      const yDelta = state.targetYMax - state.currentYMax
      if (Math.abs(yDelta) > epsilon) {
        state.currentYMax += yDelta * k
        settled = false
      } else {
        state.currentYMax = state.targetYMax
      }

      /*
       * A hard stop, so the loop cannot run forever.
       *
       * The ease is exponential and converges in a few hundred milliseconds, so
       * a run still going after SETTLE_BUDGET_MS is not easing — it is stuck.
       * That has been reported on Android: the fan kept animating after the
       * thumb was released, as though the slider were still moving. Without a
       * budget there is nothing to break the cycle, and a permanent rAF loop is
       * also the most plausible way a phone gets pushed hard enough to drop the
       * tab, which is what iOS was doing.
       *
       * Snapping to the target rather than freezing mid-ease matters: the fan
       * ends up showing the real result either way, so the worst case degrades
       * to "the animation was skipped", never "the chart is wrong".
       */
      if (!settled && time - startedAtRef.current > SETTLE_BUDGET_MS) {
        for (const key of SERIES) state.current[key].set(state.target[key])
        state.currentYMax = state.targetYMax
        settled = true
      }

      state.settled = settled
      forceRender()

      // Stop scheduling frames once nothing is moving — an idle chart should cost
      // nothing, especially on a phone.
      frameRef.current = settled ? null : requestAnimationFrame(step)
    }
  }, [result, tau, yMaxOverride])

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    },
    [],
  )

  const state = stateRef.current
  if (state === null) return null

  return {
    ages: state.ages,
    p10: state.current.p10,
    p25: state.current.p25,
    p50: state.current.p50,
    p75: state.current.p75,
    p90: state.current.p90,
    required: state.current.required,
    yMax: state.currentYMax,
    settled: state.settled,
  }
}
